import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PrimaryButton, Spinner, Stack } from "@fluentui/react";

const FaceCameraPage: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [photo, setPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /** Start camera */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access denied:", err);
    }
  };

  /** Stop camera */
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  /** Mount → start camera, Unmount → stop camera */
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  /** Capture photo */
  const capturePhoto = () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        context.drawImage(
          videoRef.current,
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        );
        const imageData = canvasRef.current.toDataURL("image/png");
        setPhoto(imageData);

        // Stop camera after photo
        stopCamera();

        // Show loader for 3 sec then navigate
        setLoading(true);
        setTimeout(() => {
          navigate("/demo/nyuujou/seikou");
        }, 2000);
      }
    }
  };

  return (
    <Stack
      verticalAlign="center"
      horizontalAlign="center"
      className="face-style"
      tokens={{ childrenGap: 20 }}
    >
      {/* Show live video if no photo and not loading */}
      {!photo && !loading && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            height: "calc(100vh - 100px)",
            objectFit: "cover",
          }}
        />
      )}

      {/* Show captured photo with loader overlay */}
      {photo && (
        <div
          style={{
            position: "relative",
            height: "calc(100vh - 100px)",
            
          }}
        >
          {/* Captured image */}
          <img
            src={photo}
            alt="Captured"
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
            }}
          />

          {/* Loader overlay */}
          {loading && (
            <div
             className="loader-camera"
            >
              <Spinner size={3} />
              <div style={{ marginTop: 12 }}>顔認証中です。少々お待ちください。</div>
            </div>
          )}
        </div>
      )}

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} width={480} height={640} hidden />

      {/* Capture button only if not loading */}
      {!loading && !photo && (
        <Stack className="camera-button">
          <PrimaryButton text="顔写真を撮影" onClick={capturePhoto} />
        </Stack>
      )}
    </Stack>
  );
};

export default FaceCameraPage;
